// statistics_clinic_08b_practice_kurtosis.md — 3 DIY cases, each giving a
// skewness plus a RAW kurtosis, so the learner has to subtract 3 to get excess
// kurtosis and then read it alongside the skewness. Deliberately includes a
// symmetric-but-heavy-tailed case, which the 08a mean-median check passes and
// only the tail check catches.

export const STATS_CLINIC_08B_TRACKER_ID = 'statistics-clinic-08b-practice-bank';

export const STATS_CLINIC_08B_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_08B_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Daily trading returns',
    scenario: "A risk analyst has set the desk's daily loss alert at three standard deviations below the average daily return, on the basis that the returns look symmetric. They want to know whether that limit is safe before it goes live.",
    tables: [{
      columns: ['dataset', 'skewness', 'raw_kurtosis'],
      rows: [['Daily return (%)', '0.1', '8.8']],
    }],
    answerGuide: {
      business_question: 'Is a -3 SD loss alert safe for this return distribution?',
      statistic_family: 'Distribution shape.',
      statistic_chosen: 'Excess kurtosis, read alongside skewness.',
      why_statistic: 'The symmetry check passes, but a +/- 3 SD rule is a statement about tail weight, which only kurtosis measures.',
      calculation: 'Excess kurtosis = 8.8 - 3 = 5.8. Skewness = 0.1, so |0.1| < 0.5.',
      result: 'Symmetric but excess kurtosis of 5.8 is far above +3, so the tails are severely heavy — the distribution is not normal.',
      meaning: 'Extreme loss days occur far more often than a bell curve predicts, even though the centre of the distribution looks well behaved.',
      decision: 'Do not ship the -3 SD alert; set the loss threshold from actual percentiles (P99 or worse-case history) and expect tail events the normal curve calls impossible.',
      caution: 'The mean-versus-median check certifies this dataset as symmetric — relying on symmetry alone would have hidden the entire risk.',
    },
    ruleTerms: {
      business_question: ['3 sd', 'loss', 'alert', 'safe'],
      statistic_family: ['distribution shape', 'shape', 'tail'],
      statistic_chosen: ['excess kurtosis', 'kurtosis'],
      why_statistic: ['tail', 'symmetry', 'only kurtosis', 'weight'],
      calculation: ['5.8', '8.8'],
      result: ['5.8', 'heavy', 'not normal'],
      meaning: ['more often', 'bell curve', 'extreme', 'centre'],
      decision: ['percentile', 'p99', 'do not', 'history'],
      caution: ['symmetric', 'alone', 'hidden', 'mean-versus-median', 'mean versus median'],
    },
    criticalTerms: { calculation: ['5.8'], result: ['5.8'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Employee engagement scores',
    scenario: 'An HR analyst wants to build a normal-based prediction interval for next quarter\'s engagement score, measured on a bounded 1-5 scale where almost everyone answers 3 or 4. They have asked whether the normal assumption holds.',
    tables: [{
      columns: ['dataset', 'skewness', 'raw_kurtosis'],
      rows: [['Engagement score (1-5)', '0.15', '1.7']],
    }],
    answerGuide: {
      business_question: 'Does the normal assumption hold well enough to build a normal-based prediction interval for engagement scores?',
      statistic_family: 'Distribution shape.',
      statistic_chosen: 'Excess kurtosis, read alongside skewness.',
      why_statistic: 'A prediction interval for a single future value depends entirely on tail behaviour, so tail weight has to be measured, not assumed from the symmetry.',
      calculation: 'Excess kurtosis = 1.7 - 3 = -1.3. Skewness = 0.15, so |0.15| < 0.5.',
      result: 'Symmetric, but excess kurtosis of -1.3 is below -1, so the tails are noticeably light — a flat, platykurtic distribution rather than a normal one.',
      meaning: 'Extreme scores are rarer than a normal curve predicts because the 1-5 scale is bounded and responses bunch on 3 and 4, so a normal interval would be too wide.',
      decision: 'Quote an interval from the observed percentiles of the bounded scale instead of a normal-based one, which would over-state how far scores can move.',
      caution: 'The light tails here are produced by the bounded collection scale, not by the underlying sentiment — check for caps, floors and truncation before interpreting flat tails as a real finding.',
    },
    ruleTerms: {
      business_question: ['normal assumption', 'prediction interval', 'hold'],
      statistic_family: ['distribution shape', 'shape', 'tail'],
      statistic_chosen: ['excess kurtosis', 'kurtosis'],
      why_statistic: ['tail', 'prediction interval', 'single', 'measured'],
      calculation: ['-1.3', '1.7'],
      result: ['-1.3', 'light', 'platykurtic', 'flat'],
      meaning: ['rarer', 'bounded', 'too wide', 'bunch'],
      decision: ['percentile', 'observed', 'instead', 'over-state', 'overstate'],
      caution: ['bounded', 'cap', 'scale', 'truncation'],
    },
    criticalTerms: { calculation: ['1.7'], result: ['1.3'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Insurance claim amounts',
    scenario: 'An insurer prices a product from the average claim amount. Most claims are small, but a few catastrophic claims dominate the total paid out each year. The pricing team wants to know whether the average is a sound basis for the premium.',
    tables: [{
      columns: ['dataset', 'skewness', 'raw_kurtosis'],
      rows: [['Claim amount (GBP)', '2.4', '12.1']],
    }],
    answerGuide: {
      business_question: 'Is the average claim amount a sound basis for pricing the premium?',
      statistic_family: 'Distribution shape.',
      statistic_chosen: 'Excess kurtosis, read alongside skewness.',
      why_statistic: 'Both halves of the normality check need testing here: the skewness shows whether the mean is being dragged, and the excess kurtosis shows how often catastrophic claims arrive.',
      calculation: 'Excess kurtosis = 12.1 - 3 = 9.1. Skewness = 2.4, so |2.4| > 1.',
      result: 'Strongly right-skewed (2.4) and severely heavy-tailed (excess 9.1) — this distribution fails both halves of the normality check.',
      meaning: 'A small number of catastrophic claims both pull the mean above the typical claim and arrive far more often than a normal curve would predict.',
      decision: 'Describe the typical claim with the median and IQR, and price the premium from the tail — modelled percentiles and catastrophe scenarios — rather than from the average claim alone.',
      caution: 'Kurtosis is driven by the rarest values, so it is unstable on small samples; state the sample size alongside it instead of quoting 9.1 as if it were precise.',
    },
    ruleTerms: {
      business_question: ['average', 'premium', 'pricing', 'sound'],
      statistic_family: ['distribution shape', 'shape', 'tail'],
      statistic_chosen: ['excess kurtosis', 'kurtosis'],
      why_statistic: ['both', 'skew', 'tail', 'normality check'],
      calculation: ['9.1', '12.1', '2.4'],
      result: ['9.1', 'skew', 'heavy', 'both'],
      meaning: ['catastrophic', 'pull the mean', 'more often', 'typical'],
      decision: ['median', 'iqr', 'tail', 'percentile'],
      caution: ['unstable', 'small sample', 'rarest', 'sample size'],
    },
    criticalTerms: { calculation: ['9.1'], result: ['9.1'] },
  },
];

export const STATS_CLINIC_08B_CONFIG = {
  trackerId: STATS_CLINIC_08B_TRACKER_ID,
  resourceKey: 'statistics_clinic_08b_practice_kurtosis',
  rubricKey: 'statistics_kurtosis',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Kurtosis Cases',
  description: 'Each case gives you a skewness and a RAW kurtosis. Convert to excess kurtosis (raw - 3), read it alongside the skewness, and say what the pair changes about the statistic or rule the business should use. Your work autosaves to your own practice record.',
  introAlert: 'Symmetry is only half of normality. Excess kurtosis = raw kurtosis - 3: within +/- 1 the normal toolkit is safe, above +1 extremes arrive far more often than the 68-95-99.7 rule predicts.',
  lenses: STATS_CLINIC_08B_LENSES,
  workingField: null,
  items: STATS_CLINIC_08B_ITEMS,
};
